import { useState } from "react";
import { Megaphone, X } from "lucide-react";

function AnnouncementsView({
  announcements = [],
  isAdmin = false,
  themeMode = "light",
  onCreateAnnouncement,
  popupAnnouncement = null,
  onClosePopup,
  onOpenAnnouncements,
  popupOnly = false
}) {
  const isDark = themeMode === "dark";
  const [form, setForm] = useState({
    title: "",
    body: "",
    category: "Güncelleme",
    priority: "normal",
    audience: "users",
    popupDisplayLimit: 1
  });
  const [isSaving, setIsSaving] = useState(false);
  const getAnnouncementTime = (announcement) => {
    const directMillis = Number(announcement?.createdAtMillis || 0);
    if (directMillis > 0) return directMillis;

    const timestampSeconds = Number(announcement?.createdAt?.seconds || 0);
    if (timestampSeconds > 0) return timestampSeconds * 1000;

    const dateText = String(announcement?.createdAtText || announcement?.date || "");
    const dateTextMatch = dateText.match(/(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
    const parsedDate = dateTextMatch
      ? new Date(
          Number(dateTextMatch[3]),
          Number(dateTextMatch[2]) - 1,
          Number(dateTextMatch[1]),
          Number(dateTextMatch[4] || 0),
          Number(dateTextMatch[5] || 0)
        ).getTime()
      : new Date(dateText || 0).getTime();

    return Number.isNaN(parsedDate) ? 0 : parsedDate;
  };

  const sortedAnnouncements = [...announcements].sort((first, second) => {
    const firstTime = getAnnouncementTime(first);
    const secondTime = getAnnouncementTime(second);

    return secondTime - firstTime;
  });

  const colors = {
    pageText: isDark ? "#e5edf6" : "#124254",
    bodyText: isDark ? "#dbeafe" : "#334155",
    muted: isDark ? "#9fb0c4" : "#64748b",
    surface: isDark ? "#162133" : "#ffffff",
    soft: isDark ? "#1e293b" : "#f8fafc",
    border: isDark ? "#334155" : "#dbe2ea",
    input: isDark ? "#101827" : "#ffffff"
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.bodyText,
    boxSizing: "border-box",
    fontWeight: 800
  };

  if (popupOnly) {
    return (
      <AnnouncementPopup
        popupAnnouncement={popupAnnouncement}
        colors={colors}
        isDark={isDark}
        onClosePopup={onClosePopup}
        onOpenAnnouncements={onOpenAnnouncements}
      />
    );
  }

  const saveAnnouncement = async () => {
    const title = form.title.trim();
    const body = form.body.trim();

    if (!title || !body) {
      alert("Duyuru başlığı ve metni boş olamaz.");
      return;
    }

    setIsSaving(true);
    try {
      await onCreateAnnouncement?.(form);
      setForm({
        title: "",
        body: "",
        category: "Güncelleme",
        priority: "normal",
        audience: "users",
        popupDisplayLimit: 1
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: "grid", gap: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, color: colors.pageText }}>Duyurular</h2>
            <p style={{ margin: "5px 0 0", color: colors.muted, fontWeight: 800 }}>
              Panel güncellemeleri, yenilikler ve ekip duyuruları
            </p>
          </div>
        </div>

        {isAdmin && (
          <div
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 18,
              padding: 18,
              boxShadow: isDark ? "0 12px 30px rgba(0,0,0,0.18)" : "0 8px 24px rgba(15,52,66,0.07)"
            }}
          >
            <h3 style={{ margin: "0 0 12px", color: colors.pageText }}>Yeni Duyuru Yayınla</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 160px 140px 170px 150px", gap: 10 }}>
              <input
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Duyuru başlığı"
                style={inputStyle}
              />
              <select
                value={form.category}
                onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
                style={inputStyle}
              >
                <option>Güncelleme</option>
                <option>Duyuru</option>
                <option>Bakım</option>
                <option>Hatırlatma</option>
              </select>
              <select
                value={form.priority}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  priority: event.target.value,
                  popupDisplayLimit: event.target.value === "high" && Number(current.popupDisplayLimit || 1) < 3
                    ? 3
                    : current.popupDisplayLimit
                }))}
                style={inputStyle}
              >
                <option value="normal">Normal</option>
                <option value="high">Önemli</option>
              </select>
              <select
                value={form.audience}
                onChange={(event) => setForm((current) => ({ ...current, audience: event.target.value }))}
                style={inputStyle}
              >
                <option value="users">Kullanıcılar</option>
                <option value="admins">Sadece Adminler</option>
                <option value="all">Herkes</option>
              </select>
              <select
                value={form.popupDisplayLimit}
                onChange={(event) => setForm((current) => ({ ...current, popupDisplayLimit: Number(event.target.value) }))}
                style={inputStyle}
              >
                <option value={1}>1 kez göster</option>
                <option value={3}>3 kez göster</option>
                <option value={5}>5 kez göster</option>
              </select>
            </div>
            <textarea
              value={form.body}
              onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
              placeholder="Duyuru metni..."
              rows={4}
              style={{ ...inputStyle, marginTop: 10, resize: "vertical", lineHeight: 1.5 }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button
                type="button"
                onClick={saveAnnouncement}
                disabled={isSaving}
                style={{
                  border: "none",
                  borderRadius: 12,
                  padding: "11px 15px",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 950,
                  cursor: isSaving ? "wait" : "pointer"
                }}
              >
                {isSaving ? "Yayınlanıyor..." : "Duyuruyu Yayınla"}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {sortedAnnouncements.length === 0 ? (
            <div style={{ color: colors.muted, fontWeight: 850 }}>Henüz duyuru yok.</div>
          ) : sortedAnnouncements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              colors={colors}
              isDark={isDark}
            />
          ))}
        </div>
      </div>

      {popupAnnouncement && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            background: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(4px)"
          }}
        >
          <div
            style={{
              width: "min(620px, 100%)",
              background: isDark ? "#111827" : "#ffffff",
              color: colors.bodyText,
              borderRadius: 22,
              border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
              boxShadow: "0 30px 80px rgba(15,23,42,0.35)",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                padding: "18px 20px",
                background: popupAnnouncement.priority === "high"
                  ? "linear-gradient(135deg, #0f766e, #2563eb)"
                  : "linear-gradient(135deg, #124254, #0f766e)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Megaphone size={24} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>
                    {popupAnnouncement.category || "Duyuru"} · {getAudienceLabel(popupAnnouncement.audience)}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 950 }}>{popupAnnouncement.title}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={onClosePopup}
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 12,
                  width: 36,
                  height: 36,
                  cursor: "pointer"
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: 22 }}>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontWeight: 750 }}>
                {popupAnnouncement.body}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 18 }}>
	                <div style={{ color: colors.muted, fontSize: 12, fontWeight: 850 }}>
	                  {popupAnnouncement.createdAtText || ""} · Pop-up {Math.min(Math.max(Number(popupAnnouncement.popupDisplayLimit || 1), 1), 5)} kez gösterilir
	                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={onClosePopup}
                    style={{
                      border: `1px solid ${colors.border}`,
                      background: colors.soft,
                      color: colors.pageText,
                      borderRadius: 12,
                      padding: "10px 13px",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Kapat
                  </button>
                  <button
                    type="button"
                    onClick={onOpenAnnouncements}
                    style={{
                      border: "none",
                      background: "#0f766e",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "10px 13px",
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    Tüm Duyurular
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AnnouncementPopup({
  popupAnnouncement,
  colors,
  isDark,
  onClosePopup,
  onOpenAnnouncements
}) {
  const popupAnnouncements = Array.isArray(popupAnnouncement)
    ? popupAnnouncement.filter(Boolean)
    : popupAnnouncement
    ? [popupAnnouncement]
    : [];

  if (popupAnnouncements.length === 0) return null;

  const hasHighPriority = popupAnnouncements.some((announcement) => announcement.priority === "high");
  const firstAnnouncement = popupAnnouncements[0];
  const isMultiple = popupAnnouncements.length > 1;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "rgba(15,23,42,0.68)",
        backdropFilter: "blur(7px)"
      }}
    >
      <div
        style={{
          width: "min(620px, 100%)",
          background: isDark ? "#111827" : "#ffffff",
          color: colors.bodyText,
          borderRadius: 22,
          border: `1px solid ${isDark ? "#334155" : "#dbe2ea"}`,
          boxShadow: "0 30px 80px rgba(15,23,42,0.35)",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            padding: "18px 20px",
            background: hasHighPriority
              ? "linear-gradient(135deg, #0f766e, #2563eb)"
              : "linear-gradient(135deg, #124254, #0f766e)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Megaphone size={24} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>
                {isMultiple
                  ? `${popupAnnouncements.length} duyuru`
                  : `${firstAnnouncement.category || "Duyuru"} · ${getAudienceLabel(firstAnnouncement.audience)}`}
              </div>
              <div style={{ fontSize: 20, fontWeight: 950 }}>
                {isMultiple ? "Yeni Duyurular" : firstAnnouncement.title}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClosePopup}
            style={{
              border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              borderRadius: 12,
              width: 36,
              height: 36,
              cursor: "pointer"
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 22 }}>
          <div style={{ display: "grid", gap: 12, maxHeight: "52vh", overflowY: "auto", paddingRight: 4 }}>
            {popupAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 16,
                  background: isDark ? "#0f172a" : "#f8fafc",
                  padding: 14
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ color: colors.pageText, fontWeight: 950, fontSize: 16 }}>
                      {announcement.title}
                    </div>
                    <div style={{ color: colors.muted, fontSize: 12, fontWeight: 850, marginTop: 3 }}>
                      {announcement.category || "Duyuru"} · {getAudienceLabel(announcement.audience)} · {announcement.createdAtText || ""}
                    </div>
                  </div>
                  {announcement.priority === "high" && (
                    <span
                      style={{
                        flex: "0 0 auto",
                        borderRadius: 999,
                        padding: "4px 8px",
                        background: isDark ? "#3b2f15" : "#fef3c7",
                        color: isDark ? "#fde68a" : "#92400e",
                        fontSize: 11,
                        fontWeight: 950
                      }}
                    >
                      Önemli
                    </span>
                  )}
                </div>

                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontWeight: 750 }}>
                  {announcement.body}
                </div>

                <div style={{ color: colors.muted, fontSize: 12, fontWeight: 850, marginTop: 10 }}>
                  Pop-up {Math.min(Math.max(Number(announcement.popupDisplayLimit || 1), 1), 5)} kez gösterilir
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 18 }}>
            <div style={{ color: colors.muted, fontSize: 12, fontWeight: 850 }}>
              {isMultiple
                ? "Tüm duyurular tek pencerede gösterildi."
                : `${firstAnnouncement.createdAtText || ""} · Pop-up ${Math.min(Math.max(Number(firstAnnouncement.popupDisplayLimit || 1), 1), 5)} kez gösterilir`}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={onClosePopup}
                style={{
                  border: `1px solid ${colors.border}`,
                  background: colors.soft,
                  color: colors.pageText,
                  borderRadius: 12,
                  padding: "10px 13px",
                  fontWeight: 900,
                  cursor: "pointer"
                }}
              >
                Kapat
              </button>
              <button
                type="button"
                onClick={onOpenAnnouncements}
                style={{
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  borderRadius: 12,
                  padding: "10px 13px",
                  fontWeight: 900,
                  cursor: "pointer"
                }}
              >
                Tüm Duyurular
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnnouncementCard({ announcement, colors, isDark }) {
  const isHighPriority = announcement.priority === "high";
  const audienceLabel = getAudienceLabel(announcement.audience);

  return (
    <div
      style={{
        background: isHighPriority
          ? (isDark ? "#102a3d" : "#f0f9ff")
          : colors.surface,
        border: isHighPriority
          ? (isDark ? "1px solid #1d4f72" : "1px solid #bae6fd")
          : `1px solid ${colors.border}`,
        borderRadius: 18,
        padding: 18
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "4px 9px",
              borderRadius: 999,
              background: isHighPriority ? "#dbeafe" : (isDark ? "#1e293b" : "#eef6f8"),
              color: isHighPriority ? "#1d4ed8" : colors.muted,
              fontSize: 11,
              fontWeight: 950,
              marginBottom: 8
            }}
          >
            {announcement.category || "Duyuru"}
          </div>
          <span
            style={{
              display: "inline-flex",
              padding: "4px 9px",
              borderRadius: 999,
              background: isDark ? "#172554" : "#eef2ff",
              color: isDark ? "#bfdbfe" : "#3730a3",
              fontSize: 11,
              fontWeight: 950,
              marginLeft: 6,
              marginBottom: 8
            }}
          >
            {audienceLabel}
          </span>
          <span
            style={{
              display: "inline-flex",
              padding: "4px 9px",
              borderRadius: 999,
              background: isDark ? "#1f2937" : "#f8fafc",
              color: colors.muted,
              fontSize: 11,
              fontWeight: 950,
              marginLeft: 6,
              marginBottom: 8
            }}
          >
            {Math.min(Math.max(Number(announcement.popupDisplayLimit || 1), 1), 5)} kez pop-up
          </span>
          <h3 style={{ margin: 0, color: colors.pageText }}>{announcement.title}</h3>
        </div>
        <div style={{ color: colors.muted, fontSize: 12, fontWeight: 850, whiteSpace: "nowrap" }}>
          {announcement.createdAtText || ""}
        </div>
      </div>
      <div style={{ marginTop: 10, whiteSpace: "pre-wrap", color: colors.bodyText, lineHeight: 1.6, fontWeight: 750 }}>
        {announcement.body}
      </div>
    </div>
  );
}

function getAudienceLabel(audience = "all") {
  if (audience === "admins") return "Sadece Adminler";
  if (audience === "users") return "Kullanıcılar";
  return "Herkes";
}

export default AnnouncementsView;
